import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  InputGroup,
  ListGroup,
  Row,
  Spinner,
} from 'react-bootstrap';
import {
  Building,
  CardList,
  Check,
  CurrencyDollar,
  Funnel,
  Grid3x3,
  ListUl,
  Person,
  PersonCircle,
  Plus,
  QuestionCircle,
  Search,
  Star,
  StarFill,
  ThreeDotsVertical,
} from 'react-bootstrap-icons';
import {
  useCreateProjectMutation,
  useGetProjectsQuery,
  useGetUsersQuery,
  useGetUserPreferencesQuery,
  useUpdateProjectMutation,
  useUpdateUserPreferencesMutation,
} from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import {
  preferencesTenantWriteSpread,
  useTenantIdForPreferences,
  useUserPreferencesQueryArg,
} from '../../hooks/useTenantIdForPreferences';
import ERMSectionHeader from './ERMSectionHeader';
import {
  buildDefaultDepartments,
  normalizeDepartmentAssignments,
  parseErmDepartmentPreferences,
} from './departmentPreferences';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
} from '../../utils/tenantTimezone';

const STAGES = ['Kick-off', 'Planning', 'Implementation', 'Review', 'Closing'];
const BOARD_COLUMN_WIDTH = 225;
const BOARD_MIN_HEIGHT = 520;
const BOARD_VIEWPORT_OFFSET = 220;
const DEFAULT_OWNER_FILTER_LABEL = 'Everyone';
const DEFAULT_SELF_OWNER_LABEL = 'PhiLL Kross';
const OWNER_EVERYONE_ID = '__everyone__';
const PROJECT_OWNER_FAVORITES_STORAGE_KEY = 'ermProjectOwnerFavorites';
const PROJECT_FILTER_FAVORITES_STORAGE_KEY = 'ermProjectFilterFavorites';

type OwnerFilterPanelTab = 'favorites' | 'owners' | 'filters';
type ProjectOwnerScopeFilterId = 'all' | 'mine' | 'unassigned';

type ProjectOwnerScopeFilterOption = {
  id: ProjectOwnerScopeFilterId;
  label: string;
  description: string;
};

const PROJECT_OWNER_SCOPE_FILTER_OPTIONS: ProjectOwnerScopeFilterOption[] = [
  { id: 'all', label: 'All projects', description: 'Show all projects' },
  { id: 'mine', label: 'My projects', description: 'Only projects assigned to you' },
  { id: 'unassigned', label: 'Unassigned projects', description: 'Only projects with no owner assigned' },
];

const normalizeOwnerKey = (value: string): string =>
  String(value || '').replace(/\(\s*you\s*\)/gi, '').trim().toLowerCase();

export interface ErmProject {
  id: string;
  title?: string;
  stage?: string;
  startDate?: string;
  endDate?: string;
  description?: string;
  status?: string;
  archived?: boolean;
  archivedAt?: string;
  ownerId?: string;
  [key: string]: unknown;
}

const toRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
);

export default function ERMProjects() {
  const { currentUser } = useUser();
  const navigate = useNavigate();
  const [view, setView] = useState<'board' | 'list' | 'grid'>('board');
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [newPhase, setNewPhase] = useState(STAGES[0]);
  const [newTemplate, setNewTemplate] = useState('None');
  const [newDeals, setNewDeals] = useState('');
  const [newContactPerson, setNewContactPerson] = useState('');
  const [newOrganization, setNewOrganization] = useState('');
  const [newLabels, setNewLabels] = useState('');
  const [newDepartmentIds, setNewDepartmentIds] = useState<string[]>([]);
  const [showOwnerFilter, setShowOwnerFilter] = useState(false);
  const [ownerFilterTab, setOwnerFilterTab] = useState<OwnerFilterPanelTab>('owners');
  const [ownerFilterSearchQuery, setOwnerFilterSearchQuery] = useState('');
  const [hoveredOwnerRowKey, setHoveredOwnerRowKey] = useState<string | null>(null);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>(OWNER_EVERYONE_ID);
  const [draggedProject, setDraggedProject] = useState<ErmProject | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [selectedOwnerScopeFilterId, setSelectedOwnerScopeFilterId] = useState<ProjectOwnerScopeFilterId>('all');
  const [favoriteOwnerKeys, setFavoriteOwnerKeys] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(PROJECT_OWNER_FAVORITES_STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed)
        ? parsed.map((entry) => String(entry || '').trim()).filter(Boolean)
        : [];
    } catch {
      return [];
    }
  });
  const [favoriteOwnerScopeFilterIds, setFavoriteOwnerScopeFilterIds] = useState<ProjectOwnerScopeFilterId[]>(() => {
    try {
      const stored = localStorage.getItem(PROJECT_FILTER_FAVORITES_STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      if (!Array.isArray(parsed)) return [];
      const allowed = new Set<ProjectOwnerScopeFilterId>(['all', 'mine', 'unassigned']);
      return parsed
        .map((entry) => String(entry) as ProjectOwnerScopeFilterId)
        .filter((entry) => allowed.has(entry));
    } catch {
      return [];
    }
  });

  const { data: projectsRaw = [], isLoading, refetch } = useGetProjectsQuery({ archived: false });
  const { data: usersData } = useGetUsersQuery(undefined);
  const tenantIdForPrefs = useTenantIdForPreferences();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);

  const [createProject, { isLoading: creating }] = useCreateProjectMutation();
  const [updateProject] = useUpdateProjectMutation();
  const [updateUserPreferences] = useUpdateUserPreferencesMutation();
  const isAdminUser = String(currentUser?.role || '').trim().toLowerCase() === 'admin';

  const projects = useMemo<ErmProject[]>(
    () => (Array.isArray(projectsRaw) ? (projectsRaw as ErmProject[]) : []),
    [projectsRaw]
  );
  const users = useMemo<Record<string, unknown>[]>(
    () => (
      Array.isArray((usersData as any)?.data?.users)
        ? ((usersData as any).data.users as Record<string, unknown>[])
        : []
    ),
    [usersData]
  );

  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );

  const allPreferences = useMemo<Record<string, unknown>>(
    () => toRecord((userPreferencesData as any)?.preferences),
    [userPreferencesData]
  );

  const {
    departments,
    projectDepartmentMap,
    focusedDepartmentIds,
    hasStoredDepartments,
  } = useMemo(
    () => parseErmDepartmentPreferences(allPreferences),
    [allPreferences]
  );

  const didAttemptDepartmentSeedRef = useRef(false);
  useEffect(() => {
    if (didAttemptDepartmentSeedRef.current) return;
    if (!userPreferencesData) return;
    didAttemptDepartmentSeedRef.current = true;
    if (!isAdminUser) return;
    if (hasStoredDepartments) return;
    void updateUserPreferences({
      ...preferencesTenantWriteSpread(tenantIdForPrefs),
      preferences: {
        ermDepartments: buildDefaultDepartments(),
      },
    }).unwrap().catch((error) => {
      console.error('Failed to seed default ERM departments:', error);
    });
  }, [hasStoredDepartments, isAdminUser, tenantIdForPrefs, updateUserPreferences, userPreferencesData]);

  const departmentById = useMemo(() => {
    const map = new Map<string, string>();
    departments.forEach((department) => map.set(department.id, department.name));
    return map;
  }, [departments]);
  const sortedDepartments = useMemo(
    () => [...departments].sort((left, right) => (
      String(left?.name || '').localeCompare(String(right?.name || ''), undefined, { sensitivity: 'base' })
    )),
    [departments]
  );

  const validDepartmentIds = useMemo(
    () => new Set(departments.map((department) => department.id)),
    [departments]
  );
  const focusedDepartmentIdSet = useMemo(
    () => new Set(focusedDepartmentIds),
    [focusedDepartmentIds]
  );
  const sortedFocusedDepartmentIds = useMemo(
    () => [...focusedDepartmentIds].sort((left, right) => {
      const leftName = String(departmentById.get(left) || '');
      const rightName = String(departmentById.get(right) || '');
      return leftName.localeCompare(rightName, undefined, { sensitivity: 'base' });
    }),
    [focusedDepartmentIds, departmentById]
  );

  const getProjectDepartmentIds = useCallback((projectIdValue: string): string[] => {
    const projectId = String(projectIdValue || '').trim();
    if (!projectId) return [];
    return normalizeDepartmentAssignments(projectDepartmentMap[projectId], validDepartmentIds);
  }, [projectDepartmentMap, validDepartmentIds]);

  const getProjectDepartmentNames = useCallback((projectIdValue: string): string[] => (
    getProjectDepartmentIds(projectIdValue)
      .map((departmentId) => departmentById.get(departmentId) || '')
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
      .filter(Boolean)
  ), [departmentById, getProjectDepartmentIds]);

  const currentUserId = String(currentUser?.id || '').trim();
  const selfOwnerLabel = useMemo(() => {
    const explicitName = String(currentUser?.name || '').trim();
    const combinedName = `${String(currentUser?.firstName || '').trim()} ${String(currentUser?.lastName || '').trim()}`.trim();
    return `${explicitName || combinedName || DEFAULT_SELF_OWNER_LABEL} (you)`;
  }, [currentUser?.firstName, currentUser?.lastName, currentUser?.name]);

  const ownerOptions = useMemo(() => {
    const ownerMap = new Map<string, { id: string; label: string }>();
    ownerMap.set(OWNER_EVERYONE_ID, { id: OWNER_EVERYONE_ID, label: DEFAULT_OWNER_FILTER_LABEL });
    if (currentUserId) {
      ownerMap.set(currentUserId, { id: currentUserId, label: selfOwnerLabel });
    }

    users.forEach((userRecord) => {
      const userId = String(userRecord?.id || '').trim();
      if (!userId) return;
      const firstName = String(userRecord?.firstName || '').trim();
      const lastName = String(userRecord?.lastName || '').trim();
      const fullName = `${firstName} ${lastName}`.trim();
      const fallbackName = String(userRecord?.name || userRecord?.email || userId).trim();
      const baseLabel = fullName || fallbackName || userId;
      const label = userId === currentUserId ? selfOwnerLabel : baseLabel;
      ownerMap.set(userId, { id: userId, label });
    });

    projects.forEach((project) => {
      const ownerId = String(project?.ownerId || '').trim();
      if (!ownerId) return;
      if (ownerMap.has(ownerId)) return;
      ownerMap.set(ownerId, {
        id: ownerId,
        label: ownerId === currentUserId ? selfOwnerLabel : ownerId,
      });
    });

    return Array.from(ownerMap.values()).sort((a, b) => {
      if (a.id === OWNER_EVERYONE_ID) return -1;
      if (b.id === OWNER_EVERYONE_ID) return 1;
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });
  }, [currentUserId, projects, selfOwnerLabel, users]);

  const selectedOwnerButtonLabel = useMemo(() => {
    const selected = ownerOptions.find((option) => option.id === selectedOwnerId);
    if (!selected) return DEFAULT_OWNER_FILTER_LABEL;
    if (selected.id === OWNER_EVERYONE_ID) return DEFAULT_OWNER_FILTER_LABEL;
    return selected.label.replace(/\(\s*you\s*\)/i, '').trim() || DEFAULT_OWNER_FILTER_LABEL;
  }, [ownerOptions, selectedOwnerId]);

  const normalizedOwnerFilterSearchQuery = ownerFilterSearchQuery.trim().toLowerCase();

  const visibleOwnerOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return ownerOptions;
    return ownerOptions.filter((option) => option.label.toLowerCase().includes(normalizedOwnerFilterSearchQuery));
  }, [normalizedOwnerFilterSearchQuery, ownerOptions]);

  const visibleScopeFilterOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return PROJECT_OWNER_SCOPE_FILTER_OPTIONS;
    return PROJECT_OWNER_SCOPE_FILTER_OPTIONS.filter((option) => {
      const searchText = `${option.label} ${option.description}`.toLowerCase();
      return searchText.includes(normalizedOwnerFilterSearchQuery);
    });
  }, [normalizedOwnerFilterSearchQuery]);

  const favoriteOwnerOptions = useMemo(() => {
    if (!favoriteOwnerKeys.length) return [];
    const favorites = new Set(favoriteOwnerKeys);
    return ownerOptions.filter((option) => favorites.has(option.id));
  }, [favoriteOwnerKeys, ownerOptions]);

  const favoriteScopeFilterOptions = useMemo(() => {
    if (!favoriteOwnerScopeFilterIds.length) return [];
    const favorites = new Set<ProjectOwnerScopeFilterId>(favoriteOwnerScopeFilterIds);
    return PROJECT_OWNER_SCOPE_FILTER_OPTIONS.filter((option) => favorites.has(option.id));
  }, [favoriteOwnerScopeFilterIds]);

  const visibleFavoriteOwnerOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return favoriteOwnerOptions;
    return favoriteOwnerOptions.filter((option) => option.label.toLowerCase().includes(normalizedOwnerFilterSearchQuery));
  }, [favoriteOwnerOptions, normalizedOwnerFilterSearchQuery]);

  const visibleFavoriteScopeFilterOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return favoriteScopeFilterOptions;
    return favoriteScopeFilterOptions.filter((option) => {
      const searchText = `${option.label} ${option.description}`.toLowerCase();
      return searchText.includes(normalizedOwnerFilterSearchQuery);
    });
  }, [favoriteScopeFilterOptions, normalizedOwnerFilterSearchQuery]);

  const filtered = useMemo((): ErmProject[] => {
    let list: ErmProject[] = [...projects];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((project: ErmProject) => (project.title || '').toLowerCase().includes(q));
    }
    if (stageFilter) {
      list = list.filter((project: ErmProject) => project.stage === stageFilter);
    }
    if (selectedOwnerId !== OWNER_EVERYONE_ID) {
      list = list.filter((project: ErmProject) => String(project.ownerId || '').trim() === selectedOwnerId);
    }
    if (selectedOwnerScopeFilterId === 'mine') {
      list = list.filter((project: ErmProject) => (
        currentUserId.length > 0
          && String(project.ownerId || '').trim() === currentUserId
      ));
    } else if (selectedOwnerScopeFilterId === 'unassigned') {
      list = list.filter((project: ErmProject) => !String(project.ownerId || '').trim());
    }
    if (focusedDepartmentIds.length > 0) {
      list = list.filter((project: ErmProject) => {
        const projectId = String(project?.id || '').trim();
        if (!projectId) return false;
        const assignedDepartments = getProjectDepartmentIds(projectId);
        return assignedDepartments.some((departmentId) => focusedDepartmentIdSet.has(departmentId));
      });
    }
    return list;
  }, [
    currentUserId,
    focusedDepartmentIdSet,
    focusedDepartmentIds,
    getProjectDepartmentIds,
    projects,
    search,
    selectedOwnerId,
    selectedOwnerScopeFilterId,
    stageFilter,
  ]);

  useEffect(() => {
    try {
      localStorage.setItem(PROJECT_OWNER_FAVORITES_STORAGE_KEY, JSON.stringify(favoriteOwnerKeys));
    } catch {
      // Ignore localStorage failures in restricted browsing modes.
    }
  }, [favoriteOwnerKeys]);

  useEffect(() => {
    try {
      localStorage.setItem(PROJECT_FILTER_FAVORITES_STORAGE_KEY, JSON.stringify(favoriteOwnerScopeFilterIds));
    } catch {
      // Ignore localStorage failures in restricted browsing modes.
    }
  }, [favoriteOwnerScopeFilterIds]);

  useEffect(() => {
    if (selectedOwnerId === OWNER_EVERYONE_ID) return;
    const selectedExists = ownerOptions.some((option) => option.id === selectedOwnerId);
    if (!selectedExists) {
      setSelectedOwnerId(OWNER_EVERYONE_ID);
    }
  }, [ownerOptions, selectedOwnerId]);

  const toggleFavoriteOwner = (ownerId: string) => {
    if (!ownerId || ownerId === OWNER_EVERYONE_ID) return;
    setFavoriteOwnerKeys((prev) => {
      if (prev.includes(ownerId)) return prev.filter((entry) => entry !== ownerId);
      return [...prev, ownerId];
    });
  };

  const toggleFavoriteScopeFilter = (filterId: ProjectOwnerScopeFilterId) => {
    if (filterId === 'all') return;
    setFavoriteOwnerScopeFilterIds((prev) => {
      if (prev.includes(filterId)) return prev.filter((entry) => entry !== filterId);
      return [...prev, filterId];
    });
  };

  const byStage = useMemo(() => {
    const map: Record<string, ErmProject[]> = {};
    STAGES.forEach((stage) => { map[stage] = []; });
    filtered.forEach((project: ErmProject) => {
      const stage = project.stage || STAGES[0];
      if (!map[stage]) map[stage] = [];
      map[stage].push(project);
    });
    return map;
  }, [filtered]);
  const activeDepartmentLabel = focusedDepartmentIds.length === 0
    ? 'All departments'
    : focusedDepartmentIds.length === 1
      ? (departmentById.get(focusedDepartmentIds[0]) || '1 department')
      : `${focusedDepartmentIds.length} departments`;

  const persistDepartmentFocus = async (nextFocusedDepartmentIds: string[]) => {
    await updateUserPreferences({
      ...preferencesTenantWriteSpread(tenantIdForPrefs),
      preferences: {
        ermFocusedDepartmentIds: normalizeDepartmentAssignments(nextFocusedDepartmentIds, validDepartmentIds),
      },
    }).unwrap();
  };

  const persistDepartmentRouting = async (nextProjectDepartmentMap: Record<string, string[]>) => {
    if (!isAdminUser) return;
    await updateUserPreferences({
      ...preferencesTenantWriteSpread(tenantIdForPrefs),
      preferences: {
        ermProjectDepartments: nextProjectDepartmentMap,
      },
    }).unwrap();
  };

  const handleToggleFocusedDepartment = async (departmentId: string) => {
    const nextFocused = focusedDepartmentIdSet.has(departmentId)
      ? focusedDepartmentIds.filter((id) => id !== departmentId)
      : [...focusedDepartmentIds, departmentId];
    try {
      await persistDepartmentFocus(nextFocused);
    } catch (error) {
      console.error('Failed to update focused departments:', error);
    }
  };

  const handleClearFocusedDepartments = async () => {
    try {
      await persistDepartmentFocus([]);
    } catch (error) {
      console.error('Failed to clear focused departments:', error);
    }
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    try {
      const result: any = await createProject({
        title: newTitle.trim(),
        stage: newPhase,
        startDate: newStartDate || undefined,
        endDate: newEndDate || undefined,
      }).unwrap();
      const project = result?.data?.project ?? result?.project ?? result;
      const projectId = String(project?.id || '').trim();
      if (projectId) {
        const normalizedDepartmentIds = normalizeDepartmentAssignments(newDepartmentIds, validDepartmentIds);
        if (isAdminUser && normalizedDepartmentIds.length > 0) {
          const nextMap = { ...projectDepartmentMap, [projectId]: normalizedDepartmentIds };
          try {
            await persistDepartmentRouting(nextMap);
          } catch (error) {
            console.error('Project was created, but department mapping failed:', error);
          }
        }
        setShowCreateModal(false);
        resetCreateForm();
        navigate(`/erm/projects/${projectId}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const resetCreateForm = () => {
    setNewTitle('');
    setNewStartDate('');
    setNewEndDate('');
    setNewPhase(STAGES[0]);
    setNewTemplate('None');
    setNewDeals('');
    setNewContactPerson('');
    setNewOrganization('');
    setNewLabels('');
    setNewDepartmentIds([]);
  };

  const handleToggleNewDepartment = (departmentId: string, checked: boolean) => {
    setNewDepartmentIds((prev) => {
      const next = checked
        ? [...prev, departmentId]
        : prev.filter((id) => id !== departmentId);
      return normalizeDepartmentAssignments(next, validDepartmentIds);
    });
  };

  const handleMoveStage = async (projectId: string, stage: string) => {
    try {
      await updateProject({ projectId, stage }).unwrap();
      refetch();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDragStart = (project: ErmProject) => {
    setDraggedProject(project);
    setDragOverStage(String(project.stage || '').trim() || null);
  };

  const handleDragEnd = () => {
    setDraggedProject(null);
    setDragOverStage(null);
  };

  const handleDragOver = (event: React.DragEvent, stage: string) => {
    event.preventDefault();
    if (dragOverStage !== stage) {
      setDragOverStage(stage);
    }
  };

  const handleDragLeave = (event: React.DragEvent, stage: string) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
    setDragOverStage((current) => (current === stage ? null : current));
  };

  const handleDrop = async (stage: string) => {
    const droppedProject = draggedProject;
    if (!droppedProject) {
      setDragOverStage(null);
      return;
    }
    try {
      if (String(droppedProject.stage || '').trim() !== stage) {
        await handleMoveStage(String(droppedProject.id || '').trim(), stage);
      }
    } finally {
      setDraggedProject(null);
      setDragOverStage(null);
    }
  };

  const handleArchive = async (projectId: string) => {
    if (!window.confirm('Archive this project? You can permanently delete it from Archive.')) return;
    try {
      await updateProject({ projectId, archived: true }).unwrap();
      refetch();
    } catch (error) {
      console.error('Failed to archive project:', error);
    }
  };

  const formatDate = (value: string | undefined) => {
    if (!value) return '';
    try {
      const date = new Date(value);
      return toLocaleDateStringInTimezone(
        date,
        'en-US',
        { day: 'numeric', month: 'short' },
        tenantTimezone
      ).toUpperCase();
    } catch {
      return value;
    }
  };

  const renderProjectDepartmentBadges = (projectIdValue: string, compact = false) => {
    const names = getProjectDepartmentNames(projectIdValue);
    if (names.length === 0) {
      return (
        <span className="text-muted" style={{ fontSize: compact ? '0.66rem' : '0.72rem' }}>
          No department
        </span>
      );
    }

    const maxVisible = compact ? 2 : 3;
    const visibleNames = names.slice(0, maxVisible);
    const hiddenCount = names.length - visibleNames.length;

    return (
      <>
        {visibleNames.map((name) => (
          <Badge key={`${projectIdValue}-${name}`} bg="light" text="dark" style={{ fontSize: compact ? '0.62rem' : '0.68rem' }}>
            {name}
          </Badge>
        ))}
        {hiddenCount > 0 ? (
          <Badge bg="secondary" style={{ fontSize: compact ? '0.62rem' : '0.68rem' }}>
            +{hiddenCount}
          </Badge>
        ) : null}
      </>
    );
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="erm-projects">
      <ERMSectionHeader
        title="Projects"
        showHelp
        searchPlaceholder="Search projects"
        searchValue={search}
        onSearchChange={setSearch}
      />

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3 mt-3">
        <div className="btn-group btn-group-sm">
          <Button
            variant={view === 'board' ? 'primary' : 'outline-secondary'}
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            onClick={() => setView('board')}
          >
            <CardList size={14} />
          </Button>
          <Button
            variant={view === 'list' ? 'primary' : 'outline-secondary'}
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            onClick={() => setView('list')}
          >
            <ListUl size={14} />
          </Button>
          <Button
            variant={view === 'grid' ? 'primary' : 'outline-secondary'}
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            onClick={() => setView('grid')}
          >
            <Grid3x3 size={14} />
          </Button>
        </div>

        <Dropdown align="end">
          <Dropdown.Toggle
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            <Building size={14} className="me-1" />
            {activeDepartmentLabel}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {sortedDepartments.map((department) => {
              const isFocused = focusedDepartmentIdSet.has(department.id);
              return (
                <Dropdown.Item
                  key={department.id}
                  active={isFocused}
                  onClick={() => { void handleToggleFocusedDepartment(department.id); }}
                >
                  {isFocused ? '✓ ' : ''}{department.name}
                </Dropdown.Item>
              );
            })}
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => { void handleClearFocusedDepartments(); }}>
              Show all departments
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown
          show={showOwnerFilter}
          autoClose="outside"
          style={{ order: 99, marginLeft: 'auto' }}
          onToggle={(isOpen) => {
            setShowOwnerFilter(isOpen);
            if (isOpen) {
              setOwnerFilterSearchQuery('');
              setOwnerFilterTab('owners');
              setHoveredOwnerRowKey(null);
            } else {
              setHoveredOwnerRowKey(null);
            }
          }}
        >
          <Dropdown.Toggle
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {selectedOwnerButtonLabel}
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ width: '320px', padding: 0, borderRadius: '0.5rem', overflow: 'hidden' }}>
            <div className="p-2 border-bottom" onClick={(event) => event.stopPropagation()}>
              <InputGroup size="sm">
                <InputGroup.Text style={{ backgroundColor: '#fff', borderRight: 'none' }}>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search owner or filter"
                  value={ownerFilterSearchQuery}
                  onChange={(event) => setOwnerFilterSearchQuery(event.target.value)}
                  style={{ borderLeft: 'none', fontSize: '0.75rem' }}
                />
              </InputGroup>
            </div>
            <div className="d-flex border-bottom" onClick={(event) => event.stopPropagation()}>
              <button
                type="button"
                onClick={() => setOwnerFilterTab('favorites')}
                style={{
                  flex: 1,
                  border: 'none',
                  borderBottom: ownerFilterTab === 'favorites' ? '2px solid #0d6efd' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  color: ownerFilterTab === 'favorites' ? '#0d6efd' : '#6c757d',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  padding: '0.5rem 0.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.15rem',
                }}
              >
                <Star size={15} />
                <span>Favorites</span>
              </button>
              <button
                type="button"
                onClick={() => setOwnerFilterTab('owners')}
                style={{
                  flex: 1,
                  border: 'none',
                  borderBottom: ownerFilterTab === 'owners' ? '2px solid #0d6efd' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  color: ownerFilterTab === 'owners' ? '#0d6efd' : '#6c757d',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  padding: '0.5rem 0.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.15rem',
                }}
              >
                <Person size={15} />
                <span>Owners</span>
              </button>
              <button
                type="button"
                onClick={() => setOwnerFilterTab('filters')}
                style={{
                  flex: 1,
                  border: 'none',
                  borderBottom: ownerFilterTab === 'filters' ? '2px solid #0d6efd' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  color: ownerFilterTab === 'filters' ? '#0d6efd' : '#6c757d',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  padding: '0.5rem 0.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.15rem',
                }}
              >
                <Funnel size={15} />
                <span>Filters</span>
              </button>
            </div>
            <div style={{ maxHeight: '280px', overflowY: 'auto', overflowX: 'hidden' }} onClick={(event) => event.stopPropagation()}>
              {ownerFilterTab === 'owners' && (
                <div className="py-1">
                  {visibleOwnerOptions.length === 0 ? (
                    <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                      No owners found.
                    </div>
                  ) : (
                    visibleOwnerOptions.map((ownerOption) => {
                      const isEveryone = ownerOption.id === OWNER_EVERYONE_ID;
                      const isFavorite = favoriteOwnerKeys.includes(ownerOption.id);
                      const isSelected = selectedOwnerId === ownerOption.id;
                      const hoverKey = normalizeOwnerKey(ownerOption.label) || ownerOption.id;
                      const isRowHovered = hoveredOwnerRowKey === hoverKey;
                      const rowTextColor = isRowHovered ? '#ffffff' : '#212529';
                      return (
                        <button
                          key={ownerOption.id}
                          type="button"
                          onClick={() => {
                            setSelectedOwnerId(ownerOption.id);
                            setShowOwnerFilter(false);
                          }}
                          onMouseEnter={() => setHoveredOwnerRowKey(hoverKey)}
                          onMouseLeave={() => setHoveredOwnerRowKey((prev) => (prev === hoverKey ? null : prev))}
                          style={{
                            width: '100%',
                            border: 'none',
                            backgroundColor: isRowHovered ? '#0d6efd' : 'transparent',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.55rem 0.75rem',
                            fontSize: '0.8rem',
                            borderRadius: '0.35rem',
                            boxSizing: 'border-box',
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <Person size={14} style={{ color: isRowHovered ? '#ffffff' : '#6c757d' }} />
                            <span style={{ fontWeight: isEveryone ? 600 : 500, color: rowTextColor }}>{ownerOption.label}</span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {isEveryone ? (
                              <span style={{ color: isRowHovered ? '#ffffff' : '#adb5bd', lineHeight: 1, display: 'inline-flex' }}>
                                <Star size={14} />
                              </span>
                            ) : (
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  toggleFavoriteOwner(ownerOption.id);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    toggleFavoriteOwner(ownerOption.id);
                                  }
                                }}
                                style={{
                                  color: isFavorite ? '#f4b400' : (isRowHovered ? '#ffffff' : '#adb5bd'),
                                  lineHeight: 1,
                                  display: 'inline-flex',
                                }}
                              >
                                {isFavorite ? <StarFill size={14} /> : <Star size={14} />}
                              </span>
                            )}
                            {isSelected ? <Check size={15} style={{ color: isRowHovered ? '#ffffff' : '#111827' }} /> : null}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
              {ownerFilterTab === 'filters' && (
                <div className="py-1">
                  {visibleScopeFilterOptions.length === 0 ? (
                    <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                      No filters found.
                    </div>
                  ) : (
                    visibleScopeFilterOptions.map((filterOption) => {
                      const isSelected = selectedOwnerScopeFilterId === filterOption.id;
                      const isFavorite = favoriteOwnerScopeFilterIds.includes(filterOption.id);
                      return (
                        <button
                          key={filterOption.id}
                          type="button"
                          onClick={() => setSelectedOwnerScopeFilterId(filterOption.id)}
                          style={{
                            width: '100%',
                            border: 'none',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: '0.5rem',
                            padding: '0.6rem 0.75rem',
                          }}
                        >
                          <div>
                            <div style={{ fontSize: '0.79rem', fontWeight: 500, color: '#212529' }}>{filterOption.label}</div>
                            <div style={{ fontSize: '0.69rem', color: '#6c757d', marginTop: '0.15rem' }}>
                              {filterOption.description}
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {filterOption.id !== 'all' ? (
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  toggleFavoriteScopeFilter(filterOption.id);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    toggleFavoriteScopeFilter(filterOption.id);
                                  }
                                }}
                                style={{ color: isFavorite ? '#f4b400' : '#adb5bd', lineHeight: 1, display: 'inline-flex' }}
                              >
                                {isFavorite ? <StarFill size={14} /> : <Star size={14} />}
                              </span>
                            ) : null}
                            {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
              {ownerFilterTab === 'favorites' && (
                <div className="py-1">
                  {visibleFavoriteOwnerOptions.length === 0 && visibleFavoriteScopeFilterOptions.length === 0 ? (
                    <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                      No favorites yet.
                    </div>
                  ) : (
                    <>
                      {visibleFavoriteOwnerOptions.length > 0 ? (
                        <div className="px-3 pt-2 pb-1 text-muted" style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.02em' }}>
                          Owners
                        </div>
                      ) : null}
                      {visibleFavoriteOwnerOptions.map((ownerOption) => {
                        const isSelected = selectedOwnerId === ownerOption.id;
                        return (
                          <button
                            key={`project-favorite-owner-${ownerOption.id}`}
                            type="button"
                            onClick={() => {
                              setSelectedOwnerId(ownerOption.id);
                              setShowOwnerFilter(false);
                            }}
                            style={{
                              width: '100%',
                              border: 'none',
                              backgroundColor: 'transparent',
                              textAlign: 'left',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.55rem 0.75rem',
                              fontSize: '0.8rem',
                            }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <Person size={14} style={{ color: '#6c757d' }} />
                              <span style={{ fontWeight: 500, color: '#212529' }}>{ownerOption.label}</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <StarFill size={14} style={{ color: '#f4b400' }} />
                              {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                            </div>
                          </button>
                        );
                      })}
                      {visibleFavoriteScopeFilterOptions.length > 0 ? (
                        <div className="px-3 pt-2 pb-1 text-muted" style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.02em' }}>
                          Filters
                        </div>
                      ) : null}
                      {visibleFavoriteScopeFilterOptions.map((filterOption) => {
                        const isSelected = selectedOwnerScopeFilterId === filterOption.id;
                        return (
                          <button
                            key={`project-favorite-filter-${filterOption.id}`}
                            type="button"
                            onClick={() => setSelectedOwnerScopeFilterId(filterOption.id)}
                            style={{
                              width: '100%',
                              border: 'none',
                              backgroundColor: 'transparent',
                              textAlign: 'left',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.55rem 0.75rem',
                              fontSize: '0.8rem',
                            }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <Funnel size={14} style={{ color: '#6c757d' }} />
                              <span style={{ fontWeight: 500, color: '#212529' }}>{filterOption.label}</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <StarFill size={14} style={{ color: '#f4b400' }} />
                              {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                            </div>
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          </Dropdown.Menu>
        </Dropdown>

        <Button
          variant="primary"
          size="sm"
          className="d-flex align-items-center"
          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          onClick={() => setShowCreateModal(true)}
        >
          <Plus size={14} className="me-1" /> Project
        </Button>

        <Dropdown>
          <Dropdown.Toggle
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            Delivery <ThreeDotsVertical size={12} className="ms-1" />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {STAGES.map((stage) => (
              <Dropdown.Item key={stage} onClick={() => setStageFilter(stageFilter === stage ? null : stage)}>
                {stage} {stageFilter === stage && ' ✓'}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown>
          <Dropdown.Toggle
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            <Funnel size={14} className="me-1" /> Filter
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => setStageFilter(null)}>Clear stage filter</Dropdown.Item>
            <Dropdown.Item onClick={() => { void handleClearFocusedDepartments(); }}>
              Clear department focus
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
          {filtered.length} project{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {focusedDepartmentIds.length > 0 ? (
        <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
          <span className="text-muted" style={{ fontSize: '0.75rem' }}>Focused:</span>
          {sortedFocusedDepartmentIds.map((departmentId) => (
            <Badge key={departmentId} bg="primary" className="d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
              {departmentById.get(departmentId) || departmentId}
            </Badge>
          ))}
          <Button
            variant="link"
            className="p-0 text-decoration-none"
            style={{ fontSize: '0.75rem' }}
            onClick={() => { void handleClearFocusedDepartments(); }}
          >
            Clear
          </Button>
        </div>
      ) : null}

      {view === 'board' && (
        <div
          className="d-flex gap-2 overflow-auto pb-2"
          style={{
            minHeight: BOARD_MIN_HEIGHT,
            height: `max(${BOARD_MIN_HEIGHT}px, calc(100vh - ${BOARD_VIEWPORT_OFFSET}px))`,
          }}
        >
          {STAGES.map((stage) => (
            <div
              key={stage}
              className="flex-shrink-0 rounded border bg-light p-2 d-flex flex-column"
              onDragOver={(event) => handleDragOver(event, stage)}
              onDragLeave={(event) => handleDragLeave(event, stage)}
              onDrop={() => void handleDrop(stage)}
              style={{
                width: BOARD_COLUMN_WIDTH,
                minHeight: '100%',
                height: '100%',
                backgroundColor: dragOverStage === stage ? '#eef4ff' : '#f8f9fa',
                borderColor: dragOverStage === stage ? '#86b7fe' : '#dee2e6',
                transition: 'background-color 120ms ease, border-color 120ms ease',
              }}
            >
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">{stage}</span>
                  <Dropdown align="end">
                    <Dropdown.Toggle variant="link" size="sm" className="text-dark p-0"><ThreeDotsVertical size={14} /></Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => { resetCreateForm(); setShowCreateModal(true); }}>Add project</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                <span className="text-muted small">{byStage[stage]?.length ?? 0} project{(byStage[stage]?.length ?? 0) !== 1 ? 's' : ''}</span>
              </div>

              <div className="d-flex flex-column gap-2 flex-grow-1 overflow-auto">
                {(byStage[stage] || []).map((project: ErmProject) => {
                  const projectId = String(project.id || '').trim();
                  const isDragging = draggedProject?.id === projectId;
                  return (
                    <Card
                      key={projectId}
                      className="shadow-sm"
                      as={Link}
                      to={`/erm/projects/${projectId}`}
                      draggable
                      onDragStart={() => handleDragStart(project)}
                      onDragEnd={handleDragEnd}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        cursor: 'move',
                        opacity: isDragging ? 0.55 : 1,
                        borderColor: isDragging ? '#86b7fe' : undefined,
                      }}
                    >
                      <Card.Body className="py-2 px-3">
                        <div className="fw-semibold small text-truncate">{project.title || 'Untitled'}</div>
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {renderProjectDepartmentBadges(projectId, true)}
                        </div>
                        <div className="progress mt-1" style={{ height: 4 }}>
                          <div className="progress-bar" role="progressbar" style={{ width: '25%' }} />
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <span className="text-muted small">{formatDate(project.endDate || project.startDate)}</span>
                          <PersonCircle size={14} className="text-muted" />
                        </div>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>

              <Button
                variant="outline-secondary"
                size="sm"
                className="w-100 mt-2 rounded-2 py-3"
                style={{ minHeight: 56, borderStyle: 'dashed' }}
                onClick={() => {
                  resetCreateForm();
                  setNewPhase(stage);
                  setShowCreateModal(true);
                }}
              >
                <Plus size={20} />
              </Button>
            </div>
          ))}
        </div>
      )}

      {view === 'list' && (
        <ListGroup>
          {filtered.map((project: ErmProject) => {
            const projectId = String(project.id || '').trim();
            return (
              <ListGroup.Item key={projectId} as={Link} to={`/erm/projects/${projectId}`} action className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="fw-semibold">{project.title || 'Untitled'}</span>
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {renderProjectDepartmentBadges(projectId)}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Badge bg="secondary">{project.stage || STAGES[0]}</Badge>
                  <span className="text-muted small">{formatDate(project.endDate || project.startDate)}</span>
                  <Dropdown align="end" onClick={(event) => event.preventDefault()}>
                    <Dropdown.Toggle variant="link" size="sm" className="text-dark"><ThreeDotsVertical /></Dropdown.Toggle>
                    <Dropdown.Menu>
                      {STAGES.map((stage) => (
                        <Dropdown.Item key={stage} onClick={() => handleMoveStage(projectId, stage)}>Move to {stage}</Dropdown.Item>
                      ))}
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={() => handleArchive(projectId)}>Archive</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      )}

      {view === 'grid' && (
        <Row xs={1} md={2} lg={3} className="g-3">
          {filtered.map((project: ErmProject) => {
            const projectId = String(project.id || '').trim();
            return (
              <Col key={projectId}>
                <Card as={Link} to={`/erm/projects/${projectId}`} className="h-100 shadow-sm" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <Card.Body>
                    <div className="fw-semibold">{project.title || 'Untitled'}</div>
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      {renderProjectDepartmentBadges(projectId)}
                    </div>
                    <div className="progress mt-2" style={{ height: 4 }}>
                      <div className="progress-bar" style={{ width: '25%' }} />
                    </div>
                    <div className="d-flex justify-content-between mt-2 small text-muted">
                      <Badge bg="light" text="dark">{project.stage || STAGES[0]}</Badge>
                      {formatDate(project.endDate || project.startDate)}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {showCreateModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-semibold">Add project</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => { setShowCreateModal(false); resetCreateForm(); }} />
              </div>
              <div className="modal-body pt-2">
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center gap-1 small text-muted">
                      Select template? <QuestionCircle size={12} />
                    </Form.Label>
                    <Form.Select value={newTemplate} onChange={(event) => setNewTemplate(event.target.value)}>
                      <option>None</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">Title</Form.Label>
                    <Form.Control
                      placeholder="Project title"
                      value={newTitle}
                      onChange={(event) => setNewTitle(event.target.value)}
                    />
                  </Form.Group>
                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <Form.Label className="small">Start date</Form.Label>
                      <Form.Control
                        type="date"
                        placeholder="MM/DD/YYYY"
                        value={newStartDate}
                        onChange={(event) => setNewStartDate(event.target.value)}
                      />
                    </div>
                    <div className="col-6">
                      <Form.Label className="small">End date</Form.Label>
                      <Form.Control
                        type="date"
                        placeholder="MM/DD/YYYY"
                        value={newEndDate}
                        onChange={(event) => setNewEndDate(event.target.value)}
                      />
                    </div>
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">Board</Form.Label>
                    <Form.Select>
                      <option>Delivery</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">Phase</Form.Label>
                    <div className="d-flex align-items-center gap-1">
                      {STAGES.map((stage, index) => (
                        <React.Fragment key={stage}>
                          <button
                            type="button"
                            className="border-0 rounded flex-grow-1 p-0"
                            style={{
                              height: 8,
                              backgroundColor: index <= STAGES.indexOf(newPhase) ? '#198754' : '#e9ecef',
                              cursor: 'pointer',
                            }}
                            title={stage}
                            onClick={() => setNewPhase(stage)}
                          />
                          {index < STAGES.length - 1 && <span className="text-muted" style={{ width: 4 }} />}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="small text-muted mt-1">{newPhase}</div>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">Owner</Form.Label>
                    <Form.Select>
                      <option>You (current user)</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">Departments</Form.Label>
                    <div className="border rounded p-2" style={{ maxHeight: 160, overflowY: 'auto' }}>
                      {sortedDepartments.map((department) => (
                        <Form.Check
                          key={`new-project-${department.id}`}
                          id={`new-project-${department.id}`}
                          type="checkbox"
                          className="small mb-1"
                          label={department.name}
                          checked={newDepartmentIds.includes(department.id)}
                          disabled={!isAdminUser}
                          onChange={(event) => handleToggleNewDepartment(department.id, event.target.checked)}
                        />
                      ))}
                    </div>
                    <Form.Text className="text-muted">
                      {isAdminUser
                        ? 'Department assignment controls ERM focus and task visibility for this project.'
                        : 'Only admins can change department assignments.'}
                    </Form.Text>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small d-flex align-items-center gap-1">
                      <CurrencyDollar size={14} /> Deals
                    </Form.Label>
                    <Form.Control
                      placeholder="Deals"
                      value={newDeals}
                      onChange={(event) => setNewDeals(event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small d-flex align-items-center gap-1">
                      <Person size={14} /> Contact person
                    </Form.Label>
                    <Form.Control
                      placeholder="Contact person"
                      value={newContactPerson}
                      onChange={(event) => setNewContactPerson(event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small d-flex align-items-center gap-1">
                      <Building size={14} /> Organization
                    </Form.Label>
                    <Form.Control
                      placeholder="Organization"
                      value={newOrganization}
                      onChange={(event) => setNewOrganization(event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">Labels</Form.Label>
                    <Form.Select value={newLabels} onChange={(event) => setNewLabels(event.target.value)}>
                      <option value="">None</option>
                    </Form.Select>
                  </Form.Group>
                </Form>
              </div>
              <div className="modal-footer border-0 pt-0">
                <Button variant="light" className="text-secondary" onClick={() => { setShowCreateModal(false); resetCreateForm(); }}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleCreate} disabled={!newTitle.trim() || creating}>
                  {creating ? <Spinner animation="border" size="sm" /> : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
